import { Link, createFileRoute } from '@tanstack/react-router'
import { BookOpen, FolderSync, Loader2 } from 'lucide-react'
import { z } from 'zod'
import { SharefileMigrationPanel } from '../../../sharefile-migration/-components/SharefileMigrationPanel'
import { RecommendedFilesPanel } from './-components/RecommendedFilesPanel'
import { PageTitle } from '@/components/PageTitle'
import { GoogleDriveIcon } from '@/components/ExternalLinks'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { requireAnyOfPermissions } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'
import { useCan } from '@/hooks/permissions/use-can'

const tabSchema = z.object({
  // `.catch` so stale links (e.g. the old `?tab=templates`) fall back to the
  // default tab instead of throwing the route error boundary.
  tab: z
    .enum(['recommended-files', 'sharefile-migration'])
    .optional()
    .default('recommended-files')
    .catch('recommended-files'),
})

type GoogleDriveRouteSearch = z.infer<typeof tabSchema>

export const Route = createFileRoute(
  '/_app/backoffice/integrations/google-drive/',
)({
  component: RouteComponent,
  validateSearch: tabSchema,
  // The page hosts two tabs with different audiences, so reaching it needs
  // either key; each tab then gates on its own. This used to be resolved in the
  // component because the ShareFile allowlist lived behind an API call — now
  // that it is a permission it arrives with everything else and the guard can
  // do the work (sc-1215).
  beforeLoad: requireAnyOfPermissions(
    BACKOFFICE_PERMISSION_KEYS.googleDrive,
    BACKOFFICE_PERMISSION_KEYS.sharefileMigration,
  ),
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { tab } = Route.useSearch()
  const { has, permissionsResolved } = useCan()

  // Each tab has its own key: Recommended Files publishes to every portal
  // client, which is not the same audience as the ShareFile export console.
  const showRecommended = has(BACKOFFICE_PERMISSION_KEYS.googleDrive)
  const showSharefile = has(BACKOFFICE_PERMISSION_KEYS.sharefileMigration)

  const isResolving = !permissionsResolved

  const handleTabChange = (value: string) => {
    void navigate({
      search: (prev: GoogleDriveRouteSearch) => ({
        ...prev,
        tab: value as GoogleDriveRouteSearch['tab'],
      }),
      replace: true,
    })
  }

  const allowedTabs = [
    showRecommended ? ('recommended-files' as const) : null,
    showSharefile ? ('sharefile-migration' as const) : null,
  ].filter((value): value is GoogleDriveRouteSearch['tab'] => value !== null)
  const activeTab = (allowedTabs.includes(tab) ? tab : allowedTabs[0]) as
    | GoogleDriveRouteSearch['tab']
    | undefined

  return (
    <div className="py-4">
      <PageTitle title="Google Drive Integration" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Google Drive</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-6 flex items-center gap-2">
        <GoogleDriveIcon className="size-7 shrink-0" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Google Drive</h1>
          <p className="text-muted-foreground mt-1">
            Manage Google Drive-backed features, from customer recommended files
            to ShareFile migration.
          </p>
        </div>
      </div>

      {isResolving || !activeTab ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="overflow-x-auto">
            <TabsList>
              {showRecommended && (
                <TabsTrigger value="recommended-files" className="gap-1.5">
                  <BookOpen className="size-3.5" />
                  Customer Recommended Files
                </TabsTrigger>
              )}
              {showSharefile && (
                <TabsTrigger value="sharefile-migration" className="gap-1.5">
                  <FolderSync className="size-3.5" />
                  ShareFile Migration
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          {showRecommended && (
            <TabsContent value="recommended-files" className="mt-4 w-full">
              <RecommendedFilesPanel />
            </TabsContent>
          )}
          {showSharefile && (
            <TabsContent value="sharefile-migration" className="mt-4 w-full">
              <SharefileMigrationPanel />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  )
}
